import React, { useEffect, useMemo } from 'react';
import { Formik, FormikProps } from 'formik';
import { GenderEnum, HighestEducationEnum } from '../../types/auth';
import { useCandidateForm } from '../../hooks/useCandidateForm';
import useInitialErrors from '../../hooks/formik-initial-errors';
import { MarketPlaceCandidateFormData } from '../../types/candidate';
import { createValidationSchema } from '../../types/formValidation';
import { TextInput } from '../../components/forms/TextInput';
import { DateInput } from '../../components/forms/DateInput';
import { SelectInput } from '../../components/forms/SelectInput';
import { PhoneInput } from '../../components/forms/PhoneInput';
import { BaseFormField } from '../../components/forms/BaseFormField';
import CountryDropdown from '../../components/dropdown/country';
import StateDropdown from '../../components/dropdown/states';
import CityDropdown from '../../components/dropdown/city';
import { RadioInput } from '../../components/forms/RadioInput';
import { SingleCheckbox } from '../../components/forms/CheckBoxInput';
import ReCAPTCHA from 'react-google-recaptcha';
import { getSiteKey } from '../../lib/utils/common-functions';
import { ALLOWED_COUNTRIES } from '../../config/constants';

const initialFormData: MarketPlaceCandidateFormData = {
    FirstName: '',
    MiddleName: '',
    LastName: '',
    Email: '',
    CityDistrict: '',
    state: '',
    country: '',
    Dob: null,
    Gender: undefined,
    Phone: '',
    PostalCode: '',
    HighestEducation: '',
    recaptcha: '',
    InstitutionName: '',
    GraduationYear: '',
    UserCountryCode: '',
};

interface CandidateRegistrationFormProps {
    onSubmit: (values: MarketPlaceCandidateFormData, email: string) => Promise<void>;
    sessionData?: Partial<MarketPlaceCandidateFormData>;
    captchaEnabled?: boolean;
    isKYCRequired?: boolean;
    customEmail?: string;
    marketPlaceData?: any;
}

export const MarketPlaceCandidateRegistrationForm: React.FC<CandidateRegistrationFormProps> = ({
    onSubmit,
    sessionData = {},
    captchaEnabled = false,
    isKYCRequired = false,
    customEmail = '',
    marketPlaceData,
}) => {
    const { Email, LastName, FirstName, Phone, MiddleName, Dob, Country } = marketPlaceData || {};

    const {
        selectedDate,
        combinedLengthError,
        userPhoneCodeError,
        setUserPhoneCodeError,
        handleDateChange,
        getCombinedNameError,
        dateConstraints,
        storedDate,
        dropdownState: {
            setStateDisabled,
            setCityDisabled,
            setIso2CountryString,
            setIsCityValueAvailable,
            setIso2StateString,
            setIsStateValueAvailable,
            stateDisabled,
            cityDisabled,
            isCityValueAvailable,
            iso2CountryString,
            iso2StateString,
            isStateValueAvailable,
        },
    } = useCandidateForm();

    const formData = useMemo(() => {
        return {
            ...initialFormData,
            Email: Email ?? customEmail,
            Dob: Dob || selectedDate || storedDate || null,
            FirstName: FirstName || '',
            LastName: LastName || '',
            MiddleName: MiddleName || '',
            Phone: Phone || '',
            country: Country || '',
        };
    }, [customEmail, selectedDate, storedDate, FirstName, LastName, MiddleName, Phone, Dob, Country, Email]);

    const validationSchema = useMemo(
        () => createValidationSchema(isStateValueAvailable, isCityValueAvailable, captchaEnabled, isKYCRequired),
        [isStateValueAvailable, isCityValueAvailable, captchaEnabled, isKYCRequired]
    );

    const initialErrors = useInitialErrors(formData, validationSchema);

    const highestEducationOptions = [
        {
            value: HighestEducationEnum.HighSchoolDiploma,
            label: 'High School Diploma',
        },
        {
            value: HighestEducationEnum.AssociateDegree,
            label: "Associate's Degree",
        },
        {
            value: HighestEducationEnum.BachelorsDegree,
            label: "Bachelor's Degree",
        },
        {
            value: HighestEducationEnum.MastersDegree,
            label: "Master's Degree",
        },
        {
            value: HighestEducationEnum.DoctorateDegree,
            label: 'Doctorate Degree',
        },
    ];

    const genderOptions = [
        { value: GenderEnum.male, label: 'Male' },
        { value: GenderEnum.female, label: 'Female' },
        { value: GenderEnum.preferNotToDisclose, label: 'Prefer Not To Disclose' },
    ];

    const now = new Date().getUTCFullYear();

    const graduationYears = Array.from({ length: 66 }, (_, idx) => {
        const year = now - idx;
        return { value: year, label: year.toString() };
    });

    const handleFormSubmit = async (values: MarketPlaceCandidateFormData) => {
        if (combinedLengthError.length > 0) return;

        await onSubmit(values, Email || customEmail);
    };

    return (
        <Formik
            initialValues={formData}
            validationSchema={validationSchema}
            initialErrors={initialErrors}
            enableReinitialize
            onSubmit={handleFormSubmit}
        >
            {(formikProps: FormikProps<MarketPlaceCandidateFormData>) => {
                const { handleSubmit, values, isSubmitting, setFieldValue } = formikProps;

                const siteKey = getSiteKey();

                return (
                    <form onSubmit={handleSubmit}>
                        <div className="card lt-shadow-sm">
                            <div className="card-body">
                                <div className="container-fluid">
                                    <div className="fs-20 fw-600 lt-text-secondary">
                                        Give us the basic details about you
                                    </div>
                                </div>
                            </div>

                            <div className="card-body">
                                <div className="reg-scroll-ht">
                                    <div className="mb-3">
                                        <div className="container-fluid">
                                            <div className="row">
                                                <div className="col-md-12">
                                                    <div className="fs-18 fw-600 mb-3">Candidate Details</div>
                                                </div>

                                                <div className="col-md-6">
                                                    <TextInput
                                                        name="FirstName"
                                                        label="First Name"
                                                        placeholder="As on your Highest Educational Certificate"
                                                        required
                                                        disabled
                                                        validateName
                                                        getCombinedNameError={getCombinedNameError}
                                                    />
                                                </div>

                                                <div className="col-md-6">
                                                    <TextInput
                                                        name="MiddleName"
                                                        label="Middle Name"
                                                        disabled
                                                        placeholder="As on your Highest Educational Certificate"
                                                        validateName
                                                        getCombinedNameError={getCombinedNameError}
                                                    />
                                                </div>

                                                <div className="col-md-6">
                                                    <TextInput
                                                        name="LastName"
                                                        label="Last Name"
                                                        placeholder="As on your Highest Educational Certificate"
                                                        required
                                                        disabled
                                                        validateName
                                                        getCombinedNameError={getCombinedNameError}
                                                    />
                                                    {combinedLengthError && (
                                                        <small className="text-danger fs-12">
                                                            {combinedLengthError}
                                                        </small>
                                                    )}
                                                </div>

                                                <div className="col-md-6">
                                                    <DateInput
                                                        name="Dob"
                                                        label="Date of Birth"
                                                        required
                                                        disabled
                                                        minDate={dateConstraints.minDate}
                                                        maxDate={dateConstraints.maxDate}
                                                        onDateChange={handleDateChange}
                                                        storedDate={storedDate}
                                                        placeholder="As on your Highest Educational Certificate"
                                                    />
                                                </div>

                                                <div className="col-md-6">
                                                    <SelectInput
                                                        name="Gender"
                                                        label="Gender"
                                                        options={genderOptions}
                                                        placeholder="Select Your Gender"
                                                        required
                                                    />
                                                </div>

                                                {/* <div className="col-md-6">
                                                    <TextInput
                                                        name="Email"
                                                        label="Email"
                                                        placeholder="As on your Highest Educational Certificate"
                                                        required
                                                        disabled
                                                        validateName
                                                        type="email"
                                                        inputMode="email"
                                                        getCombinedNameError={getCombinedNameError}
                                                    />
                                                </div> */}

                                                <div className="col-md-6">
                                                    <PhoneInput
                                                        countryCodeName="UserCountryCode"
                                                        phoneName="Phone"
                                                        label="Contact Number"
                                                        required
                                                        disabled
                                                        onPhoneError={setUserPhoneCodeError}
                                                    />
                                                    {userPhoneCodeError && (
                                                        <small className="text-danger fs-12">
                                                            {userPhoneCodeError}
                                                        </small>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="container-fluid">
                                            <div className="row mt-3">
                                                <div className="col-12">
                                                    <div className="fs-18 fw-600 mb-3">Location Details</div>
                                                </div>

                                                <div className="col-md-6 mb-4">
                                                    <BaseFormField name={'country'} label="Country" showLabel required>
                                                        {({ field, error, values, setFieldValue, setFieldTouched }) => (
                                                            <>
                                                                <CountryDropdown
                                                                    id="country"
                                                                    setFieldValue={setFieldValue}
                                                                    countryDisabled={!!Country}
                                                                    countryFieldValue={field.value}
                                                                    setStateDisabled={setStateDisabled}
                                                                    setCityDisabled={setCityDisabled}
                                                                    setIso2CountryString={setIso2CountryString}
                                                                    error={field.value === '' ? error : ''}
                                                                    allowedCountries={ALLOWED_COUNTRIES}
                                                                    onCountrySelectionChange={()=>{}}
                                                                />
                                                                {typeof error === 'string' && (
                                                                    <small className="text-danger fs-12">{error}</small>
                                                                )}
                                                            </>
                                                        )}
                                                    </BaseFormField>
                                                </div>
                                                <div className="col-md-6 mb-4">
                                                    <BaseFormField name={'state'} label="State" showLabel required>
                                                        {({ field, error, values, setFieldValue, setFieldTouched }) => (
                                                            <>
                                                                <StateDropdown
                                                                    id="state"
                                                                    setFieldValue={setFieldValue}
                                                                    setIsStateValueAvailable={setIsStateValueAvailable}
                                                                    setIsCityValueAvailable={setIsCityValueAvailable}
                                                                    setStateDisabled={setStateDisabled}
                                                                    stateFieldValue={field.value}
                                                                    stateDisabled={stateDisabled}
                                                                    setCityDisabled={setCityDisabled}
                                                                    Iso2CountryString={iso2CountryString}
                                                                    setIso2StateString={setIso2StateString}
                                                                    error={
                                                                        isStateValueAvailable && field.value === ''
                                                                            ? error
                                                                            : ''
                                                                    }
                                                                />

                                                                {typeof error === 'string' && (
                                                                    <small className="text-danger fs-12">{error}</small>
                                                                )}
                                                            </>
                                                        )}
                                                    </BaseFormField>
                                                </div>
                                                <div className="col-md-6 mb-4">
                                                    <BaseFormField
                                                        name={'CityDistrict'}
                                                        label="City"
                                                        showLabel
                                                        required
                                                    >
                                                        {({ field, error, values, setFieldValue, setFieldTouched }) => (
                                                            <>
                                                                <CityDropdown
                                                                    id="city"
                                                                    setFieldValue={setFieldValue}
                                                                    setIsCityValueAvailable={setIsCityValueAvailable}
                                                                    setCityDisabled={setCityDisabled}
                                                                    cityFieldValue={field.value}
                                                                    cityDisabled={cityDisabled}
                                                                    iso2CountryString={iso2CountryString}
                                                                    iso2StateString={iso2StateString}
                                                                    error={
                                                                        isCityValueAvailable && field.value === ''
                                                                            ? error
                                                                            : ''
                                                                    }
                                                                />

                                                                {typeof error === 'string' && (
                                                                    <small className="text-danger fs-12">{error}</small>
                                                                )}
                                                            </>
                                                        )}
                                                    </BaseFormField>
                                                </div>
                                                <div className="col-md-6 mb-4">
                                                    <TextInput
                                                        name="PostalCode"
                                                        label="Postal / Zip Code"
                                                        placeholder="Enter Postal / Zip Code"
                                                        required
                                                        maxLength={10}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="container-fluid">
                                            <div className="row mt-3">
                                                <div className="col-12">
                                                    <div className="fs-18 fw-600 mb-3">Education</div>
                                                </div>

                                                <div className="col-md-6">
                                                    <SelectInput
                                                        name="HighestEducation"
                                                        label="Higher Education"
                                                        options={highestEducationOptions}
                                                        placeholder="Select Highest Education"
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <TextInput
                                                        name="InstitutionName"
                                                        label="Institution Name"
                                                        placeholder="Enter Institution Name as per Educational Certificate"
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <SelectInput
                                                        name="GraduationYear"
                                                        label="Graduation Year"
                                                        options={graduationYears}
                                                        placeholder="Select Year as per Educational Certificate"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {captchaEnabled && (
                                        <div className="mb-3">
                                            <div className="container-fluid">
                                                <div className="row mt-3">
                                                    <div className="col-md-12">
                                                        <div className="col-md-6 mb-4">
                                                            <BaseFormField name={'recaptcha'} label="">
                                                                {({
                                                                    field,
                                                                    error,
                                                                    values,
                                                                    setFieldValue,
                                                                    setFieldTouched,
                                                                }) => (
                                                                    <>
                                                                        <ReCAPTCHA
                                                                            sitekey={siteKey}
                                                                            onChange={(value) => {
                                                                                setFieldTouched(field.name);
                                                                                setFieldValue(field.name, value);
                                                                            }}
                                                                        />
                                                                        {typeof error === 'string' && (
                                                                            <small className="text-danger fs-12">
                                                                                {error}
                                                                            </small>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </BaseFormField>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="d-flex flex-row-reverse card-body">
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="btn btn-primary social-next-button"
                                            onClick={() => {
                                                if (selectedDate) {
                                                    setFieldValue('Dob', selectedDate);
                                                }
                                            }}
                                        >
                                            {isSubmitting && <span className="spinner-border spinner-border-sm" />}
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                );
            }}
        </Formik>
    );
};

export default MarketPlaceCandidateRegistrationForm;
